/**
 * FloatingPreviewWindow — the draggable/resizable multitask window chrome
 * (2026-08-02 directive; same-day amendment: sizing is ENTIRELY customizable —
 * every edge and corner resizes, plus maximize/restore and double-click-header
 * maximize). Drag by the header, focus bumps z-order; content is the shared
 * PreviewBody engine (attachments) or the sandboxed 3D scene (resolver-backed).
 * Geometry math is pure (windowGeometry.ts) and clamped so a window can never
 * be dragged beyond recovery.
 */
import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { usePreview, PreviewBody, ToolButton, FullFrame } from './PreviewContent';
import { resolveThreeDScene, ResolvedScene } from './resolve3d';
import { injectRuntimeGuard } from './sceneRuntimeGuard';
import { OpenChatBesideButton, type PreviewOriginContext } from './previewOrigin';
import {
  applyDrag,
  applyEdgeResize,
  clampWindowRect,
  clearReservedRight,
  maximizedRect,
  ResizeEdge,
  WindowRect,
} from './windowGeometry';
import {
  DOCKED_COMPANION_MIN_VIEWPORT_PX,
  companionReservedRightPx,
  useIsChatDockedCompanion,
} from '../GlobalChatSystem/dockedCompanion';
import type { FloatingWindowState } from './FloatingPreviewContext';

const Shell = styled.div`
  position: fixed;
  background: #101623; color: #e8edf6;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 12px;
  display: flex; flex-direction: column; overflow: hidden;
  box-shadow: 0 18px 48px rgba(0, 0, 0, 0.55);
`;

const Header = styled.div`
  display: flex; align-items: center; gap: 8px;
  padding: 8px 10px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  cursor: move; user-select: none; flex: 0 0 auto;
  touch-action: none;
`;

const Title = styled.div`
  flex: 1; min-width: 0; font-size: 13px; font-weight: 600;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
`;

const Body = styled.div`
  flex: 1; min-height: 0; display: flex; position: relative; background: #0a0f1a;
`;

/** Grab strips on every edge + corner (entirely-customizable sizing). */
const EdgeHandle = styled.div<{ $cursor: string }>`
  position: absolute; z-index: 3; touch-action: none;
  cursor: ${(p) => p.$cursor};
`;

const CornerGlyph = styled.div`
  position: absolute; right: 4px; bottom: 4px; width: 8px; height: 8px;
  pointer-events: none;
  border-right: 2px solid rgba(255, 255, 255, 0.35);
  border-bottom: 2px solid rgba(255, 255, 255, 0.35);
`;

const Center = styled.div`
  margin: auto; color: #93a1b8; font-size: 13px; text-align: center; padding: 18px;
`;

const EDGE_PX = 7;
const CORNER_PX = 16;

const RESIZE_HANDLES: Array<{ edge: ResizeEdge; cursor: string; style: React.CSSProperties }> = [
  { edge: 'n', cursor: 'ns-resize', style: { top: 0, left: CORNER_PX, right: CORNER_PX, height: EDGE_PX } },
  { edge: 's', cursor: 'ns-resize', style: { bottom: 0, left: CORNER_PX, right: CORNER_PX, height: EDGE_PX } },
  { edge: 'e', cursor: 'ew-resize', style: { top: CORNER_PX, bottom: CORNER_PX, right: 0, width: EDGE_PX } },
  { edge: 'w', cursor: 'ew-resize', style: { top: CORNER_PX, bottom: CORNER_PX, left: 0, width: EDGE_PX } },
  { edge: 'ne', cursor: 'nesw-resize', style: { top: 0, right: 0, width: CORNER_PX, height: CORNER_PX } },
  { edge: 'nw', cursor: 'nwse-resize', style: { top: 0, left: 0, width: CORNER_PX, height: CORNER_PX } },
  { edge: 'se', cursor: 'nwse-resize', style: { bottom: 0, right: 0, width: CORNER_PX, height: CORNER_PX } },
  { edge: 'sw', cursor: 'nesw-resize', style: { bottom: 0, left: 0, width: CORNER_PX, height: CORNER_PX } },
];

const AttachmentContent: React.FC<{
  file?: File | null;
  serialized?: any;
  context?: PreviewOriginContext | null;
}> = ({ file, serialized, context }) => {
  // context restored 2026-08-24: without it a floated preview could not use
  // the server's locator-free resolve (name + messageId), so a metadata-only
  // attachment degraded to the download card the moment it was popped out.
  const p = usePreview(file, serialized, context);
  return <PreviewBody p={p} wrap />;
};

const ThreeDContent: React.FC<{ artifactId: string }> = ({ artifactId }) => {
  const [scene, setScene] = useState<ResolvedScene | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    resolveThreeDScene(artifactId)
      .then((s) => { if (!cancelled) setScene(s); })
      .catch(() => { if (!cancelled) setError('This 3D model could not be loaded.'); });
    return () => { cancelled = true; };
  }, [artifactId]);
  if (error) return <Center>{error}</Center>;
  if (!scene) return <Center>Loading 3D model…</Center>;
  return (
    <FullFrame
      title={scene.title || 'Interactive 3D model'}
      sandbox="allow-scripts allow-pointer-lock"
      // Sandboxed frames have an opaque origin, so the pointer-lock
      // permissions-policy must be granted with * — without it Chrome
      // (incognito especially) rejects requestPointerLock silently and
      // "click to walk through" does nothing.
      allow="pointer-lock *; fullscreen *"
      // R13: guard injected like Preview3DPage and PreviewContent's scene3d
      // lane — a scene that dies at boot must explain itself in every shell
      // (2026-08-09 "failure has no face" contract).
      srcDoc={injectRuntimeGuard(scene.html)}
    />
  );
};

export interface FloatingPreviewWindowProps {
  state: FloatingWindowState;
  onClose: () => void;
  onFocus: () => void;
  onRect: (rect: WindowRect) => void;
}

export const FloatingPreviewWindow: React.FC<FloatingPreviewWindowProps> = ({ state, onClose, onFocus, onRect }) => {
  const { rect, z, payload } = state;
  const bodyRef = useRef<HTMLDivElement | null>(null);
  // Live "the chat holds the right rail" bit — maximize sizes around it.
  const isChatCompanion = useIsChatDockedCompanion();
  const gesture = useRef<{
    mode: 'drag' | 'resize';
    edge: ResizeEdge;
    startX: number;
    startY: number;
    origin: WindowRect;
  } | null>(null);
  // Maximize/restore: remember where the window was so Restore puts it back.
  const restoreRef = useRef<WindowRect | null>(null);
  const [isMaximized, setIsMaximized] = useState(false);

  const beginGesture = (mode: 'drag' | 'resize', edge: ResizeEdge = 'se') => (e: React.PointerEvent) => {
    e.preventDefault();
    onFocus();
    gesture.current = { mode, edge, startX: e.clientX, startY: e.clientY, origin: rect };
    (e.target as Element).setPointerCapture?.(e.pointerId);
  };

  const onPointerMove = (e: React.PointerEvent) => {
    const g = gesture.current;
    if (!g) return;
    const dx = e.clientX - g.startX;
    const dy = e.clientY - g.startY;
    // Any manual drag/resize leaves maximized mode (the window is the user's now).
    if (isMaximized && (dx !== 0 || dy !== 0)) setIsMaximized(false);
    const next = g.mode === 'drag'
      ? applyDrag(g.origin, dx, dy, window.innerWidth, window.innerHeight)
      : applyEdgeResize(g.origin, g.edge, dx, dy, window.innerWidth, window.innerHeight);
    onRect(next);
  };

  const endGesture = () => { gesture.current = null; };

  const toggleMaximize = () => {
    if (isMaximized && restoreRef.current) {
      onRect(clampWindowRect(restoreRef.current, window.innerWidth, window.innerHeight));
      setIsMaximized(false);
    } else {
      restoreRef.current = rect;
      // Maximize fills the screen the chat is NOT holding (2026-08-24), so
      // going big beside a docked pane gives the side-by-side view instead
      // of burying the conversation the user was watching.
      onRect(maximizedRect(
        window.innerWidth,
        window.innerHeight,
        companionReservedRightPx({ docked: isChatCompanion, viewportWidth: window.innerWidth }),
      ));
      setIsMaximized(true);
    }
  };

  /**
   * "Open chat" clicked: the pane is about to take the right rail, so a
   * window sitting there would be the very thing it covers. Slide out from
   * under it, keeping this window's size wherever it still fits. The rail's
   * footprint is asked for as docked (not the live bit) because the dock has
   * been REQUESTED and has not landed yet — and only above the split's
   * viewport floor, since a phone never docks at all.
   */
  const stepAsideForChat = () => {
    if (window.innerWidth < DOCKED_COMPANION_MIN_VIEWPORT_PX) return;
    setIsMaximized(false);
    onRect(clearReservedRight(
      rect,
      window.innerWidth,
      window.innerHeight,
      // The rail at the width the person keeps it (dockedWidth.ts), not the default.
      companionReservedRightPx({ docked: true, viewportWidth: window.innerWidth }),
    ));
  };

  // Keep the window recoverable across viewport resizes.
  useEffect(() => {
    const onWindowResize = () => onRect(clampWindowRect(rect, window.innerWidth, window.innerHeight));
    window.addEventListener('resize', onWindowResize);
    return () => window.removeEventListener('resize', onWindowResize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rect.x, rect.y, rect.width, rect.height]);

  const subtitle = payload.kind === 'attachment'
    ? undefined
    : 'Interactive 3D model';

  return (
    <Shell
      style={{ left: rect.x, top: rect.y, width: rect.width, height: rect.height, zIndex: z }}
      onPointerDown={onFocus}
      role="dialog"
      aria-label={`Floating preview: ${payload.title}`}
      // portalClickGuard marker: interacting with a floating preview must never
      // read as an outside click that closes/collapses the chat (2026-08-02).
      data-prest0n-portal="true"
    >
      <Header
        onPointerDown={beginGesture('drag')}
        onPointerMove={onPointerMove}
        onPointerUp={endGesture}
        onPointerCancel={endGesture}
        onDoubleClick={toggleMaximize}
      >
        <Title title={payload.title}>{payload.title}{subtitle ? ` — ${subtitle}` : ''}</Title>
        {/* The file's own conversation, beside it — same control, same copy
            as the modal shell (previewOrigin.tsx). Only attachments carry an
            origin; a 3D artifact window has no conversation behind it. */}
        {payload.kind === 'attachment' && (
          <OpenChatBesideButton origin={payload.context} onOpened={stepAsideForChat} />
        )}
        <ToolButton
          onPointerDown={(e: React.PointerEvent) => e.stopPropagation()}
          onClick={toggleMaximize}
          aria-label={isMaximized ? 'restore window size' : 'maximize window'}
        >
          {isMaximized ? 'Restore' : 'Maximize'}
        </ToolButton>
        <ToolButton
          onPointerDown={(e: React.PointerEvent) => e.stopPropagation()}
          onClick={() => bodyRef.current?.requestFullscreen?.().catch(() => undefined)}
        >
          Fullscreen
        </ToolButton>
        <ToolButton
          onPointerDown={(e: React.PointerEvent) => e.stopPropagation()}
          onClick={onClose}
          aria-label="close floating preview"
        >
          ✕
        </ToolButton>
      </Header>
      <Body ref={bodyRef}>
        {payload.kind === 'attachment'
          ? <AttachmentContent file={payload.file} serialized={payload.serialized} context={payload.context} />
          : <ThreeDContent artifactId={payload.artifactId} />}
      </Body>
      {RESIZE_HANDLES.map(({ edge, cursor, style }) => (
        <EdgeHandle
          key={edge}
          $cursor={cursor}
          style={style}
          data-resize-edge={edge}
          onPointerDown={beginGesture('resize', edge)}
          onPointerMove={onPointerMove}
          onPointerUp={endGesture}
          onPointerCancel={endGesture}
          aria-label={`resize ${edge}`}
        />
      ))}
      <CornerGlyph />
    </Shell>
  );
};

export default FloatingPreviewWindow;
